import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useMuted } from '@/lib/voice';
import { IDS } from '@/constants/testIds';
import { APP } from '@/constants/brand';
import GuidePickerModal from '@/components/GuidePickerModal';
import { Wrench, History, Plus, LogOut, BarChart3, UserCog, GraduationCap, ShieldCheck, Cpu, ArrowLeft, Volume2, VolumeX } from 'lucide-react';

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showPicker, setShowPicker] = useState(false);
  const [muted, setMuted] = useMuted();

  const handleLogout = () => {
    navigate('/', { replace: true });
    setTimeout(() => logout(), 0);
  };

  // Show back on every page. If there's nowhere to go, fall back to a sensible root.
  const showBack = true;

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(user ? '/app' : '/');
  };

  return (
    <header className="border-b border-zinc-800 bg-zinc-950/90 backdrop-blur sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 sm:gap-2 min-w-0">
          {showBack && (
            <button
              onClick={handleBack}
              data-testid="header-back-btn"
              className="inline-flex items-center gap-1 text-zinc-400 hover:text-amber-500 border border-zinc-800 hover:border-amber-500 px-2 py-2 font-mono text-xs uppercase tracking-widest transition-colors shrink-0"
              title="Back"
              aria-label="Back"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Back</span>
            </button>
          )}
          <Link
            to={user ? '/app' : '/'}
            data-testid={IDS.headerLogo}
            className="flex items-center gap-2 group min-w-0"
          >
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-amber-500 flex items-center justify-center shrink-0">
              <Wrench size={18} className="text-zinc-950" strokeWidth={2.5} />
            </div>
            <div className="leading-none min-w-0 hidden sm:block">
              <div className="font-display text-lg sm:text-2xl text-zinc-100 tracking-wide truncate">{APP.name}</div>
              <div className="font-mono text-[9px] sm:text-[10px] text-amber-500 uppercase tracking-[0.2em] mt-0.5 hidden xl:block">
                Every trade · Every fix
              </div>
            </div>
          </Link>
        </div>

        {user ? (
          <nav className="flex items-center gap-1 sm:gap-3 shrink-0">
            <Link
              to="/app"
              data-testid={IDS.headerNew}
              className="hidden md:inline-flex items-center gap-2 text-zinc-300 hover:text-amber-500 text-sm font-mono uppercase tracking-wide"
              title="New Fix"
            >
              <Plus size={16} /> New Fix
            </Link>
            <Link
              to="/history"
              data-testid={IDS.headerHistory}
              className="inline-flex items-center gap-1 sm:gap-2 text-zinc-300 hover:text-amber-500 text-xs sm:text-sm font-mono uppercase tracking-wide p-1"
              title="History"
            >
              <History size={16} /> <span className="hidden md:inline">History</span>
            </Link>
            <Link
              to="/nameplate"
              data-testid="header-nameplate"
              className="inline-flex items-center gap-1 sm:gap-2 text-zinc-300 hover:text-sky-400 text-xs sm:text-sm font-mono uppercase tracking-wide p-1"
              title="Nameplate scanner"
            >
              <Cpu size={16} /> <span className="hidden md:inline">Scan</span>
            </Link>
            <button
              onClick={() => setShowPicker(true)}
              data-testid="header-change-guide"
              className="inline-flex items-center gap-1 sm:gap-2 text-zinc-300 hover:text-amber-500 text-xs sm:text-sm font-mono uppercase tracking-wide p-1"
              title="Change your DIY buddy"
            >
              <UserCog size={16} /> <span className="hidden md:inline">Buddy</span>
            </button>
            {user.subscription_status === 'active' ? (
              <Link
                to="/account/subscribe"
                data-testid="header-pro-badge"
                className="hidden sm:inline-flex items-center gap-1 border border-lime-500/40 bg-lime-500/10 text-lime-400 px-2 py-1 font-mono text-[10px] uppercase tracking-widest hover:bg-lime-500/20"
                title={`Pro · ${user.subscription_days_left} days left`}
              >
                <ShieldCheck size={12} /> Pro
              </Link>
            ) : (
              <Link
                to="/account/subscribe"
                data-testid="header-upgrade"
                className="hidden sm:inline-flex items-center gap-1 border border-amber-500/40 bg-amber-500/10 text-amber-400 px-2 py-1 font-mono text-[10px] uppercase tracking-widest hover:bg-amber-500/20"
                title={user.subscription_status === 'trial' ? `Trial · ${user.subscription_days_left}d left` : 'Upgrade to Pro'}
              >
                <GraduationCap size={12} /> {user.subscription_status === 'trial' ? `Trial ${user.subscription_days_left}d` : 'Upgrade'}
              </Link>
            )}
            {user.is_sponsor_admin && (
              <Link
                to="/admin/sponsor"
                data-testid={IDS.adminNavLink}
                className="inline-flex items-center gap-1 sm:gap-2 text-lime-400 hover:text-lime-300 text-xs sm:text-sm font-mono uppercase tracking-wide p-1"
                title="Sponsor stats"
              >
                <BarChart3 size={16} /> <span className="hidden md:inline">Stats</span>
              </Link>
            )}
            <div data-testid={IDS.headerUser} className="hidden xl:flex items-center gap-2 text-sm text-zinc-400 border-l border-zinc-800 pl-4">
              <span className="font-mono uppercase text-xs">{user.name}</span>
            </div>
            <button
              onClick={() => setMuted(!muted)}
              data-testid="header-mute-toggle"
              className={`p-2 border border-zinc-800 hover:border-amber-500 transition-colors ${muted ? 'text-zinc-500' : 'text-amber-400'}`}
              title={muted ? 'Buddy voice is off — click to turn on' : 'Buddy voice is on — click to mute'}
              aria-label={muted ? 'Unmute buddy' : 'Mute buddy'}
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            <button
              onClick={handleLogout}
              data-testid={IDS.headerLogout}
              className="text-zinc-400 hover:text-amber-500 p-2"
              title="Log out"
            >
              <LogOut size={18} />
            </button>
          </nav>
        ) : (
          <nav className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Link to="/auth?mode=login" data-testid={IDS.landingCtaLogin} className="text-zinc-300 hover:text-amber-500 text-xs sm:text-sm font-mono uppercase">
              Log In
            </Link>
            <Link to="/auth?mode=signup" className="btn-primary text-xs sm:text-sm">
              Sign Up
            </Link>
          </nav>
        )}
      </div>
      {showPicker && (
        <GuidePickerModal onClose={() => setShowPicker(false)} dismissible={true} />
      )}
    </header>
  );
}
